import { useMemo, useState } from 'react';
import type { EmployeeListResponse, Task } from 'src/api/generated';
import type { TagDropdownOption } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import type { TaskFiltersState } from '../../../types/agenda.types';
import { TaskAssigneeType, TaskStatus } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import { useEmployees } from 'src/modules/shared/hooks/api/automations/employees/employees';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import {
  PiUserLight,
  PiFunnelX,
  PiCalendar,
  PiList,
  PiCaretDown,
  PiFunnelSimple,
} from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import {
  getMenuItemContentClasses,
  getMenuItemContentColor,
  getMenuItemContentWeight,
} from 'src/modules/shared/styles/dropdown.styles';

const USER_ASSIGNEE_TYPE = TaskAssigneeType.USER;
const AI_EMPLOYEE_ASSIGNEE_TYPE = TaskAssigneeType.AI_EMPLOYEE;
type AgendaViewMode = 'list' | 'calendar';

interface TaskFiltersProps {
  currentView: AgendaViewMode;
  filters: TaskFiltersState;
  onFiltersChange: (filters: TaskFiltersState) => void;
  onClearFilters: () => void;
  onViewChange: (view: AgendaViewMode) => void;
}

export const TaskFilters: React.FC<TaskFiltersProps> = ({
  currentView,
  filters,
  onFiltersChange,
  onClearFilters,
  onViewChange,
}) => {
  const { t } = useI18n();
  const { users } = useAllUsers();
  const { employees } = useEmployees();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  // Note: Conversation dropdown is simplified for now and can be enhanced later
  // const { conversations } = useFunnelConversationDocuments({}, false); // Disabled for performance

  const statusOptions: TagDropdownOption[] = useMemo(
    () => [
      {
        id: 'all',
        content: t('calendar.agenda.filters.status.all'),
        selected: !filters.status,
      },
      {
        id: TaskStatus.SCHEDULED,
        content: t('calendar.agenda.filters.status.scheduled'),
        selected: filters.status === TaskStatus.SCHEDULED,
      },
      {
        id: TaskStatus.COMPLETED,
        content: t('calendar.agenda.filters.status.completed'),
        selected: filters.status === TaskStatus.COMPLETED,
      },
      {
        id: TaskStatus.CANCELLED,
        content: t('calendar.agenda.filters.status.cancelled'),
        selected: filters.status === TaskStatus.CANCELLED,
      },
    ],
    [filters.status, t]
  );

  const assigneeOptions: TagDropdownOption[] = useMemo(() => {
    const options: TagDropdownOption[] = [
      {
        id: 'all',
        content: t('calendar.agenda.filters.assignee.all'),
        selected: !filters.assignedToType && !filters.assignedToId,
      },
    ];

    // Add user options
    const userOptions = users.map((user) => ({
      id: `user-${user.id}`,
      content: (
        <div className="flex items-center gap-2">
          <PiUserLight className="h-4 w-4" />
          <Text
            size="paragraph"
            weight={getMenuItemContentWeight(false)}
            color={getMenuItemContentColor(false)}
            className={getMenuItemContentClasses()}
          >
            {formatFullName(user.firstName, user.lastName)}
          </Text>
        </div>
      ),
      selected:
        filters.assignedToType === USER_ASSIGNEE_TYPE &&
        filters.assignedToId === user.id.toString(),
    }));

    // Add employee options
    const employeeOptions = employees.map(
      (employee: EmployeeListResponse['items'][number]) => ({
        id: `ai_employee-${employee.employeeId}`,
        content: (
          <div className="flex items-center gap-2">
            <AiRobotOutlineIcon className="h-4 w-4" />
            <Text
              size="paragraph"
              weight={getMenuItemContentWeight(false)}
              color={getMenuItemContentColor(false)}
              className={getMenuItemContentClasses()}
            >
              {employee.name}
            </Text>
          </div>
        ),
        selected:
          filters.assignedToType === AI_EMPLOYEE_ASSIGNEE_TYPE &&
          filters.assignedToId === employee.employeeId,
      })
    );

    return [...options, ...userOptions, ...employeeOptions];
  }, [users, employees, filters.assignedToType, filters.assignedToId, t]);

  const conversationOptions: TagDropdownOption[] = useMemo(
    () => [
      {
        id: 'all',
        content: t('calendar.agenda.filters.task.all'),
        selected: !filters.conversationId,
      },
      {
        id: 'none',
        content: t('calendar.agenda.filters.task.none'),
        selected: filters.conversationId === 'none',
      },
      // TODO: Add actual conversations when needed
      // For now, we'll keep it simple and just show the basic options
    ],
    [filters.conversationId, t]
  );

  const viewOptions = useMemo(
    () => [
      {
        id: 'list' as const,
        label: t('calendar.agenda.view.list'),
        icon: <PiList className="h-4 w-4" />,
      },
      {
        id: 'calendar' as const,
        label: t('calendar.agenda.view.calendar'),
        icon: <PiCalendar className="h-4 w-4" />,
      },
    ],
    [t]
  );

  const selectedView =
    viewOptions.find((option) => option.id === currentView) ?? viewOptions[0];

  const handleStatusFilter = (option: TagDropdownOption) => {
    const status =
      option.id === 'all' ? undefined : (option.id as Task['status']);
    onFiltersChange({ ...filters, status });
  };

  const handleAssigneeFilter = (option: TagDropdownOption) => {
    if (option.id === 'all') {
      onFiltersChange({
        ...filters,
        assignedToType: undefined,
        assignedToId: undefined,
      });
    } else {
      const [type, id] = String(option.id).split('-');
      onFiltersChange({
        ...filters,
        assignedToType: type as TaskFiltersState['assignedToType'],
        assignedToId: id,
      });
    }
  };

  const handleConversationFilter = (option: TagDropdownOption) => {
    const conversationId =
      option.id === 'all'
        ? undefined
        : option.id === 'none'
          ? ('none' as const)
          : Number(option.id);
    onFiltersChange({ ...filters, conversationId });
  };

  const hasActiveFilters = !!(
    filters.status ??
    filters.assignedToType ??
    filters.assignedToId ??
    filters.conversationId ??
    filters.dueBefore ??
    filters.dueAfter
  );

  const filtersButtonLabel = hasActiveFilters
    ? t('calendar.agenda.filters.activeButton')
    : t('calendar.agenda.filters.button');

  /**
   * Get the display label for status filter based on selected option
   */
  const getStatusLabel = () => {
    const selectedOption = statusOptions.find((opt) => opt.selected);

    if (!selectedOption) {
      return t('calendar.agenda.filters.status.all');
    }

    if (typeof selectedOption.content === 'string') {
      return selectedOption.content;
    }

    return t('calendar.agenda.filters.status.all');
  };

  /**
   * Get the display label for assignee filter based on selected option
   */
  const getAssigneeLabel = () => {
    const selectedOption = assigneeOptions.find((opt) => opt.selected);

    if (!selectedOption) {
      return t('calendar.agenda.filters.assignee.all');
    }

    // Handle string content
    if (typeof selectedOption.content === 'string') {
      return selectedOption.content;
    }

    // Handle JSX content - extract name from assignee options
    if (filters.assignedToType === USER_ASSIGNEE_TYPE && filters.assignedToId) {
      const assignedUser = users.find(
        (user) => user.id.toString() === filters.assignedToId
      );
      return assignedUser
        ? formatFullName(assignedUser.firstName, assignedUser.lastName)
        : t('calendar.agenda.filters.assignee.all');
    }

    if (
      filters.assignedToType === AI_EMPLOYEE_ASSIGNEE_TYPE &&
      filters.assignedToId
    ) {
      const employee = employees.find(
        (e: EmployeeListResponse['items'][number]) =>
          e.employeeId === filters.assignedToId
      );
      return employee
        ? employee.name
        : t('calendar.agenda.filters.assignee.all');
    }

    return t('calendar.agenda.filters.assignee.all');
  };

  /**
   * Get the display label for conversation filter based on selected option
   */
  const getConversationLabel = () => {
    const selectedOption = conversationOptions.find((opt) => opt.selected);

    if (!selectedOption) {
      return t('calendar.agenda.filters.task.all');
    }

    // Handle string content
    if (typeof selectedOption.content === 'string') {
      return selectedOption.content;
    }

    // Handle JSX content - fallback to default label
    return t('calendar.agenda.filters.task.all');
  };

  const stackedFilterControls = (
    <>
      <MenuDropdown
        label={getStatusLabel()}
        items={statusOptions.map((option) => ({
          ...option,
          onClick: () => handleStatusFilter(option),
        }))}
        appearance="outline"
        size="md"
        className="w-full justify-between"
        handlerContainerClassName="w-full"
      />

      <MenuDropdown
        label={getAssigneeLabel()}
        items={assigneeOptions.map((option) => ({
          ...option,
          onClick: () => handleAssigneeFilter(option),
        }))}
        appearance="outline"
        size="md"
        className="w-full justify-between"
        handlerContainerClassName="w-full"
      />

      <MenuDropdown
        label={getConversationLabel()}
        items={conversationOptions.map((option) => ({
          ...option,
          onClick: () => handleConversationFilter(option),
        }))}
        appearance="outline"
        size="md"
        className="w-full justify-between"
        handlerContainerClassName="w-full"
      />

      {hasActiveFilters && (
        <Button
          variant="primary"
          appearance="soft"
          size="sm"
          onClick={() => {
            onClearFilters();
            setIsMobileFiltersOpen(false);
          }}
          className="w-full sm:w-auto"
        >
          <PiFunnelX className="w-4 h-4" />
          <Text size="small">{t('calendar.agenda.filters.clear')}</Text>
        </Button>
      )}
    </>
  );

  return (
    <>
      <PageFiltersBar contentClassName="lg:flex-nowrap">
        <MenuDropdown
          icon={selectedView.icon}
          label={selectedView.label}
          items={viewOptions.map((option) => ({
            content: option.label,
            icon: option.icon,
            selected: option.id === currentView,
            onClick: () => onViewChange(option.id),
          }))}
          appearance="outline"
          size="md"
          className="w-full justify-between sm:w-[176px] lg:flex-shrink-0"
          handlerContainerClassName="w-full sm:w-auto lg:flex-shrink-0"
        />

        <div className="min-w-0 w-full lg:hidden sm:w-[176px] md:w-[220px]">
          <Button
            type="button"
            variant="secondary"
            appearance="outline"
            size="md"
            className="relative w-full !justify-start gap-2 px-3 text-left"
            onClick={() => setIsMobileFiltersOpen(true)}
          >
            <PiFunnelSimple className="h-4 w-4" />
            <span className="whitespace-nowrap truncate">
              {filtersButtonLabel}
            </span>
            <PiCaretDown className="h-4 w-4 absolute right-3" />
          </Button>
        </div>

        <div className="hidden lg:grid lg:min-w-0 lg:flex-1 lg:grid-cols-[minmax(0,20rem)_minmax(0,20rem)_minmax(0,20rem)_auto] lg:justify-start lg:items-center lg:gap-4">
          <MenuDropdown
            label={getStatusLabel()}
            items={statusOptions.map((option) => ({
              ...option,
              onClick: () => handleStatusFilter(option),
            }))}
            appearance="outline"
            size="md"
            className="w-full max-w-[20rem] justify-between"
            handlerContainerClassName="w-full"
          />

          <MenuDropdown
            label={getAssigneeLabel()}
            items={assigneeOptions.map((option) => ({
              ...option,
              onClick: () => handleAssigneeFilter(option),
            }))}
            appearance="outline"
            size="md"
            className="w-full max-w-[20rem] justify-between"
            handlerContainerClassName="w-full"
          />

          <MenuDropdown
            label={getConversationLabel()}
            items={conversationOptions.map((option) => ({
              ...option,
              onClick: () => handleConversationFilter(option),
            }))}
            appearance="outline"
            size="md"
            className="w-full max-w-[20rem] justify-between"
            handlerContainerClassName="w-full"
          />

          {hasActiveFilters && (
            <Button
              variant="primary"
              appearance="soft"
              size="sm"
              onClick={onClearFilters}
              className="justify-self-start whitespace-nowrap"
            >
              <PiFunnelX className="w-4 h-4" />
              <Text size="small">{t('calendar.agenda.filters.clear')}</Text>
            </Button>
          )}
        </div>
      </PageFiltersBar>

      <Modal
        isOpen={isMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
        header={
          <Text as="h3" size="h5" weight="semibold">
            {t('shared.dropdown.mobileTitle')}
          </Text>
        }
        dialogClassName="!max-w-sm"
        bodyClassName="p-0"
        body={
          <div className="w-full border-0 bg-white px-4 py-4">
            <div className="flex flex-col gap-3">{stackedFilterControls}</div>
          </div>
        }
      />
    </>
  );
};
